import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getMeeting, getTasks, generateFollowUp, getFollowUps, sendFollowUp, updateFollowUp, syncMeetingToHubSpot, updateHubSpotDealStatus, deleteHubSpotDeal, getHubSpotStatus, getMeetingCalendarUrl, updateMeetingStatus, getGoogleCalendarUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  Phone, 
  Video, 
  Users,
  Sparkle,
  EnvelopeSimple,
  PaperPlaneTilt,
  SpinnerGap,
  PencilSimple,
  CheckCircle,
  CalendarPlus,
  ArrowSquareOut,
  Trophy,
  XCircle,
  Trash,
  Tag,
  Copy
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

export default function MeetingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingFollowUp, setGeneratingFollowUp] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [hubspotConfigured, setHubspotConfigured] = useState(false);
  const [syncingHubspot, setSyncingHubspot] = useState(false);
  const [hubspotResult, setHubspotResult] = useState(null);
  const [updatingDealStatus, setUpdatingDealStatus] = useState(false);
  const [deletingDeal, setDeletingDeal] = useState(false);

  useEffect(() => {
    fetchMeetingData();
    checkHubspotStatus();
  }, [id]);

  const checkHubspotStatus = async () => {
    try {
      const response = await getHubSpotStatus();
      setHubspotConfigured(response.data.configured);
    } catch (error) {
      console.error('Failed to check HubSpot status:', error);
    }
  };

  const fetchMeetingData = async () => {
    try {
      const [meetingRes, tasksRes, followUpsRes] = await Promise.all([
        getMeeting(id),
        getTasks(),
        getFollowUps(id)
      ]);
      
      setMeeting(meetingRes.data);
      setTasks(tasksRes.data.filter(t => t.meeting_id === id));
      setFollowUps(followUpsRes.data);
      
      if (meetingRes.data.client_email) {
        setRecipientEmail(meetingRes.data.client_email);
      }
    } catch (error) {
      console.error('Failed to fetch meeting:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFollowUp = async () => {
    setGeneratingFollowUp(true);
    try {
      const response = await generateFollowUp(id);
      setFollowUps(prev => [response.data, ...prev]);
      setSelectedFollowUp(response.data);
    } catch (error) {
      console.error('Failed to generate follow-up:', error);
    } finally {
      setGeneratingFollowUp(false);
    }
  };

  const handleSendFollowUp = async () => {
    if (!selectedFollowUp || !recipientEmail) return;
    
    setSendingEmail(true);
    try {
      await sendFollowUp(selectedFollowUp.id, recipientEmail);
      setShowSendDialog(false);
      fetchMeetingData();
    } catch (error) {
      console.error('Failed to send follow-up:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEditFollowUp = () => {
    if (!selectedFollowUp) return;
    setEditedSubject(selectedFollowUp.subject);
    setEditedBody(selectedFollowUp.body);
    setEditingFollowUp(true);
  };

  const handleSaveFollowUp = async () => {
    if (!selectedFollowUp) return;
    try {
      await updateFollowUp(selectedFollowUp.id, {
        subject: editedSubject,
        body: editedBody
      });
      setSelectedFollowUp(prev => ({
        ...prev,
        subject: editedSubject,
        body: editedBody
      }));
      setEditingFollowUp(false);
      fetchMeetingData();
    } catch (error) {
      console.error('Failed to update follow-up:', error);
    }
  };

  const handleSyncToHubspot = async () => {
    setSyncingHubspot(true);
    setHubspotResult(null);
    try {
      const response = await syncMeetingToHubSpot(id);
      setHubspotResult(response.data);
      fetchMeetingData();
    } catch (error) {
      console.error('Failed to sync to HubSpot:', error);
      setHubspotResult({ error: error.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncingHubspot(false);
    }
  };

  const handleUpdateDealStatus = async (status) => {
    setUpdatingDealStatus(true);
    try {
      await updateHubSpotDealStatus(id, status);
      fetchMeetingData();
      setHubspotResult({ success: true, message: `Deal marked as ${status}` });
    } catch (error) {
      console.error('Failed to update deal status:', error);
      setHubspotResult({ error: error.response?.data?.detail || 'Update failed' });
    } finally {
      setUpdatingDealStatus(false);
    }
  };

  const handleDeleteDeal = async () => {
    if (!window.confirm('Are you sure you want to delete this deal from HubSpot? This cannot be undone.')) {
      return;
    }
    
    setDeletingDeal(true);
    try {
      await deleteHubSpotDeal(id);
      fetchMeetingData();
      setHubspotResult({ success: true, message: 'Deal deleted from HubSpot' });
    } catch (error) {
      console.error('Failed to delete deal:', error);
      setHubspotResult({ error: error.response?.data?.detail || 'Delete failed' });
    } finally {
      setDeletingDeal(false);
    }
  };

  const handleAddToCalendar = () => {
    window.open(getMeetingCalendarUrl(id), '_blank');
  };

  const handleDealStatusChange = async (newStatus) => {
    try {
      await updateMeetingStatus(id, newStatus);
      fetchMeetingData();
    } catch (error) {
      console.error('Failed to update deal status:', error);
    }
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={18} />;
      case 'video': return <Video size={18} />;
      case 'in-person': return <Users size={18} />;
      default: return <Calendar size={18} />;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'prospecting': return 'bg-gray-100 text-gray-700';
      case 'qualification': return 'bg-blue-50 text-blue-700';
      case 'proposal': return 'bg-amber-50 text-amber-700';
      case 'negotiation': return 'bg-purple-50 text-purple-700';
      case 'closed': return 'bg-green-50 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDealStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-[#007AFF] text-white';
      case 'pending': return 'bg-amber-500 text-white';
      case 'closed_won': return 'bg-green-600 text-white';
      case 'closed_lost': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getDealStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Open';
      case 'pending': return 'Pending';
      case 'closed_won': return 'Won';
      case 'closed_lost': return 'Lost';
      default: return 'Open';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Meeting not found</h2>
        <Link to="/meetings" className="text-[#007AFF] hover:underline mt-2 inline-block">
          Back to meetings
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-entrance" data-testid="meeting-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-4"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F] truncate">
              {meeting.client_name}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-[#86868B]">
              {meeting.client_company && (
                <span className="font-medium">{meeting.client_company}</span>
              )}
              <Badge className={`${getStageColor(meeting.deal_stage)} rounded-xl text-xs capitalize`}>
                {meeting.deal_stage || 'N/A'}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Deal Status Selector */}
            <div className="flex items-center gap-2">
              <Tag size={18} className="text-[#86868B] hidden sm:block" />
              <Select 
                value={meeting.deal_status || 'open'} 
                onValueChange={handleDealStatusChange}
              >
                <SelectTrigger 
                  className={`w-28 sm:w-32 h-10 rounded-xl border-0 ${getDealStatusColor(meeting.deal_status || 'open')}`}
                  data-testid="deal-status-select"
                >
                  <SelectValue>{getDealStatusLabel(meeting.deal_status || 'open')}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="closed_won">Won</SelectItem>
                  <SelectItem value="closed_lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={handleGenerateFollowUp}
              disabled={generatingFollowUp}
              className="btn-primary h-10 sm:h-12 px-4 sm:px-6 rounded-xl text-sm sm:text-base"
              data-testid="generate-followup-btn"
            >
              {generatingFollowUp ? (
                <>
                  <SpinnerGap size={18} className="mr-1 sm:mr-2 animate-spin" />
                  <span className="hidden sm:inline">Generating...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <Sparkle size={18} className="mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Generate Follow-up</span>
                  <span className="sm:hidden">Follow-up</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
        <Button 
          onClick={handleAddToCalendar}
          variant="outline"
          className="btn-secondary h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
          data-testid="add-to-calendar-btn"
        >
          <CalendarPlus size={16} className="mr-1 sm:mr-2" />
          .ics
        </Button>
        
        <Button 
          onClick={() => {
            const startDate = new Date(meeting.meeting_date);
            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
            const description = `Client: ${meeting.client_name}\nCompany: ${meeting.client_company || 'N/A'}\n\nKey Points:\n${meeting.key_points}`;
            const url = getGoogleCalendarUrl(
              `Meeting: ${meeting.client_name}`,
              startDate,
              endDate,
              description,
              meeting.client_company || ''
            );
            window.open(url, '_blank');
          }}
          variant="outline"
          className="btn-secondary h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
          data-testid="add-to-google-calendar-btn"
        >
          <Calendar size={16} className="mr-1 sm:mr-2" />
          Google Cal
        </Button>
        
        {hubspotConfigured && (
          <>
            <Button 
              onClick={handleSyncToHubspot}
              disabled={syncingHubspot}
              variant="outline"
              className="btn-secondary h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
              data-testid="sync-hubspot-btn"
            >
              {syncingHubspot ? (
                <SpinnerGap size={16} className="mr-1 animate-spin" />
              ) : (
                <ArrowSquareOut size={16} className="mr-1 sm:mr-2" />
              )}
              HubSpot
            </Button>
            
            {meeting.hubspot_deal_id && (
              <>
                <Button 
                  onClick={() => handleUpdateDealStatus('won')}
                  disabled={updatingDealStatus || deletingDeal}
                  className="bg-green-600 hover:bg-green-700 text-white h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
                  data-testid="mark-won-btn"
                >
                  <Trophy size={16} className="mr-1" />
                  Won
                </Button>
                <Button 
                  onClick={() => handleUpdateDealStatus('lost')}
                  disabled={updatingDealStatus || deletingDeal}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
                  data-testid="mark-lost-btn"
                >
                  <XCircle size={16} className="mr-1" />
                  Lost
                </Button>
                <Button 
                  onClick={handleDeleteDeal}
                  disabled={deletingDeal || updatingDealStatus}
                  variant="outline"
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-red-600 hover:border-red-300 h-9 sm:h-10 px-3 sm:px-4 rounded-xl text-sm"
                  data-testid="delete-deal-btn"
                >
                  {deletingDeal ? (
                    <SpinnerGap size={16} className="mr-1 animate-spin" />
                  ) : (
                    <Trash size={16} className="mr-1" />
                  )}
                  Delete
                </Button>
              </>
            )}
          </>
        )}
      </div>

      {/* HubSpot Result Message */}
      {hubspotResult && (
        <div className={`mb-6 p-4 border ${hubspotResult.error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {hubspotResult.error ? (
            <p>{hubspotResult.error}</p>
          ) : (
            <div>
              {hubspotResult.actions?.map((action, i) => (
                <p key={i}>{action}</p>
              ))}
              {hubspotResult.message && <p>{hubspotResult.message}</p>}
              {hubspotResult.existing_deals && hubspotResult.existing_deals.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Existing deals found:</p>
                  {hubspotResult.existing_deals.map((deal, i) => (
                    <p key={i} className="text-sm">• {deal.name} (Stage: {deal.stage})</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Meeting Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Meeting Info Card */}
          <div className="card-base p-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
              Meeting Details
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3 p-3 bg-gray-50">
                <Calendar size={18} className="text-[#007AFF]" />
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wide">Date</p>
                  <p className="font-medium text-[#1D1D1F]">
                    {format(parseISO(meeting.meeting_date), 'MMMM d, yyyy')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-50">
                {getMeetingTypeIcon(meeting.meeting_type)}
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wide">Type</p>
                  <p className="font-medium text-[#1D1D1F] capitalize">{meeting.meeting_type}</p>
                </div>
              </div>
              
              {meeting.client_email && (
                <div className="flex items-center gap-3 p-3 bg-gray-50">
                  <EnvelopeSimple size={18} className="text-[#007AFF]" />
                  <div>
                    <p className="text-xs text-[#86868B] uppercase tracking-wide">Email</p>
                    <p className="font-medium text-[#1D1D1F]">{meeting.client_email}</p>
                  </div>
                </div>
              )}
              
              {meeting.deal_value && (
                <div className="flex items-center gap-3 p-3 bg-gray-50">
                  <div className="w-[18px] h-[18px] flex items-center justify-center text-[#34C759] font-bold">$</div>
                  <div>
                    <p className="text-xs text-[#86868B] uppercase tracking-wide">Deal Value</p>
                    <p className="font-medium text-[#34C759]">${meeting.deal_value.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Key Points */}
          <div className="card-base p-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
              Key Points Discussed
            </h2>
            <p className="text-[#86868B] whitespace-pre-wrap">{meeting.key_points}</p>
          </div>

          {/* Next Steps */}
          {meeting.next_steps && (
            <div className="card-base p-6">
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
                Next Steps
              </h2>
              <p className="text-[#86868B] whitespace-pre-wrap">{meeting.next_steps}</p>
            </div>
          )}

          {/* Notes */}
          {meeting.notes && (
            <div className="card-base p-6">
              <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
                Additional Notes
              </h2>
              <p className="text-[#86868B] whitespace-pre-wrap">{meeting.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar - Tasks & Follow-ups */}
        <div className="space-y-6">
          {/* Tasks */}
          <div className="card-base p-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
              Tasks ({tasks.length})
            </h2>
            
            {tasks.length === 0 ? (
              <p className="text-sm text-[#86868B]">
                Tasks will be auto-generated from meeting details.
              </p>
            ) : (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div key={task.id} className="p-3 border border-gray-200">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-[#1D1D1F]">{task.title}</p>
                      <Badge className={`${task.status === 'completed' ? 'status-completed' : 'status-pending'} rounded-xl text-xs`}>
                        {task.status}
                      </Badge>
                    </div>
                    {task.description && (
                      <p className="text-xs text-[#86868B] mt-1">{task.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Follow-ups */}
          <div className="card-base p-6">
            <h2 className="text-lg font-semibold text-[#1D1D1F] mb-4">
              Follow-ups ({followUps.length})
            </h2>
            
            {followUps.length === 0 ? (
              <p className="text-sm text-[#86868B]">
                Click "Generate Follow-up" to create an AI-powered email.
              </p>
            ) : (
              <div className="space-y-3">
                {followUps.map(followUp => (
                  <button
                    key={followUp.id}
                    onClick={() => setSelectedFollowUp(followUp)}
                    className={`w-full text-left p-3 border transition-colors ${
                      selectedFollowUp?.id === followUp.id 
                        ? 'border-[#007AFF] bg-[#007AFF]/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`followup-item-${followUp.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-[#1D1D1F] truncate">{followUp.subject}</p>
                      <Badge className={`${followUp.status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'} rounded-xl text-xs`}>
                        {followUp.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#86868B] mt-1">
                      {format(parseISO(followUp.created_at), 'MMM d, h:mm a')}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Follow-up Preview */}
      {selectedFollowUp && (
        <div className="mt-8 card-base p-6" data-testid="followup-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">
              Follow-up Preview
            </h2>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={handleEditFollowUp}
                className="btn-secondary h-9 px-4 rounded-xl"
                data-testid="edit-followup-btn"
              >
                <PencilSimple size={16} className="mr-2" />
                Edit
              </Button>
              {selectedFollowUp.status === 'draft' && (
                <Button 
                  onClick={() => setShowSendDialog(true)}
                  className="btn-primary h-9 px-4 rounded-xl"
                  data-testid="send-followup-btn"
                >
                  <PaperPlaneTilt size={16} className="mr-2" />
                  Send Email
                </Button>
              )}
            </div>
          </div>
          
          {editingFollowUp ? (
            <div className="space-y-4">
              <div>
                <Label className="text-[#1D1D1F] font-medium">Subject</Label>
                <Input
                  value={editedSubject}
                  onChange={(e) => setEditedSubject(e.target.value)}
                  className="input-base h-10 rounded-xl mt-1"
                />
              </div>
              <div>
                <Label className="text-[#1D1D1F] font-medium">Body</Label>
                <Textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  rows={10}
                  className="input-base rounded-xl mt-1 font-mono text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveFollowUp}
                  className="btn-primary h-9 px-4 rounded-xl"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Save Changes
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setEditingFollowUp(false)}
                  className="btn-secondary h-9 px-4 rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wide">Subject</p>
                  <p className="font-medium text-[#1D1D1F] mt-1">{selectedFollowUp.subject}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedFollowUp.subject);
                    toast.success('Subject copied to clipboard');
                  }}
                  className="h-8 px-2 text-[#86868B] hover:text-[#007AFF]"
                  data-testid="copy-subject-btn"
                >
                  <Copy size={16} />
                </Button>
              </div>
              
              <div className="ai-preview whitespace-pre-wrap relative">
                {selectedFollowUp.body}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedFollowUp.body);
                    toast.success('Email body copied to clipboard');
                  }}
                  className="absolute top-2 right-2 h-8 px-3 bg-white hover:bg-gray-50 text-[#86868B] hover:text-[#007AFF]"
                  data-testid="copy-body-btn"
                >
                  <Copy size={14} className="mr-1" />
                  Copy
                </Button>
              </div>
              
              <div className="mt-4 flex items-center gap-2">
                <Button
                  onClick={() => {
                    const fullEmail = `Subject: ${selectedFollowUp.subject}\n\n${selectedFollowUp.body}`;
                    navigator.clipboard.writeText(fullEmail);
                    toast.success('Full email copied to clipboard! Paste into your email client.');
                  }}
                  className="btn-primary h-10 px-6 rounded-xl"
                  data-testid="copy-full-email-btn"
                >
                  <Copy size={18} className="mr-2" />
                  Copy Full Email
                </Button>
                <span className="text-sm text-[#86868B]">
                  Copy and paste into Gmail, Outlook, or your email client
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Follow-up Email</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#1D1D1F] font-medium">Recipient Email</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="client@company.com"
                className="input-base h-12 rounded-xl"
                data-testid="recipient-email-input"
              />
            </div>
            
            <p className="text-sm text-[#86868B]">
              The follow-up email will be sent to the specified address.
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSendDialog(false)}
              className="btn-secondary rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendFollowUp}
              disabled={!recipientEmail || sendingEmail}
              className="btn-primary rounded-xl"
              data-testid="confirm-send-btn"
            >
              {sendingEmail ? (
                <>
                  <SpinnerGap size={16} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={16} className="mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
